import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
  getNextStatus,
  hasAdministrationRights,
  isUserAnEventActor,
  hasUserAlreadyPerformedValidation,
  isCurrentUserEventInstructor,
  isUserEventOwner,
  isUserAnEventActorWithValidations
} from 'utils/event-utils';
import { EventFormStore } from 'stores/events/event-form-store';
import { connect } from 'redux-scaffolding-ts';
import { resolve } from 'inversify-react';
import { IdentityService, Roles } from 'services/identity-service';
import ChangeStatusPopup from '../popups/change-status-popup/change-status-popup';
import { ToastComponent } from 'site/pages/landing-pages/util/toast-component';
import { ChangeEventInstructorDto } from 'stores/events/events-store';
import { EventTypeCategory } from 'stores/configuration/events-workflow/event-types-store';

export interface CloseButtonProps extends WithTranslation {
  onClosed?: () => void;
  eventFormStore?: EventFormStore;
  readOnly: boolean;
}

export interface CloseButtonState {
  showChangeStatusModal: boolean;
  showAlertCloseModal: boolean;
}

@connect(['eventFormStore', EventFormStore])
class CloseButtonTw extends Component<CloseButtonProps, CloseButtonState> {
  state: CloseButtonState = {
    showChangeStatusModal: false,
    showAlertCloseModal: false
  };

  @resolve(IdentityService)
  private identityService: IdentityService;

  handleOnCloseClicked = async () => {
    const { eventFormStore, onClosed, t } = this.props;
    const item = eventFormStore.state.item;

    // Tipo GEA/GMA: validación de soporte obligatorio antes de cerrar
    if (isGEA(item.eventType) || isGMA(item.eventType)) {
      const result = await eventFormStore.validateSupprotDetailsClose();
      if (!result.isSuccess) {
        (result?.messages || []).forEach(msg => ToastComponent({ text: msg?.body, type: 'warning-toast' }));
        return;
      }
    }

    // Admin u Owner: directo a modal
    if (this.identityService.isInRole(Roles.Admin) || isUserEventOwner(item)) {
      const isVacations = EventTypeCategory[item.eventType?.eventTypeCategory] === EventTypeCategory.Vacations;
      if (item.hasInstructors && !isVacations) {
        const hasInvalidInstructors = (item.instructors || []).some(
          inst => inst == null || !inst.workingDays || inst.workingDays <= 0 || !inst.workingHours || inst.workingHours <= 0
        );
        if (hasInvalidInstructors) {
          ToastComponent({
            text: t('All instructors must have Working Days and Working Hours filled in before closing the event'),
            type: 'warning-toast'
          });
          return;
        }
      }
      this.setState({ showChangeStatusModal: true });
      return;
    }

    // Si es instructor, componer items
    const items: ChangeEventInstructorDto[] = [];
    if (this.identityService.isInRole(Roles.Instructor)) {
      if (!this.instructorCanClosedEvent()) {
        this.setState({ showAlertCloseModal: true });
        return;
      }
      const instructorInstances = item.instructors.filter(({ user: { id } }) => id === this.identityService.userId);
      const wrongWDsWHs = (instructorInstances || []).some(({ workingDays: WD, workingHours: WH }) => !WD || !WH || WD <= 0 || WH <= 0);
      const isTypeVactions = EventTypeCategory[item.eventType.eventTypeCategory] === EventTypeCategory.Vacations;

      if (!isTypeVactions && wrongWDsWHs) {
        ToastComponent({ text: t('Invalid Working Days or Working Hours inputs'), type: 'warning-toast' });
        return;
      }

      (instructorInstances || []).forEach(
        ({ location: { id: locationId }, workingDays, workingHours, period, role, user: { id: instructorId }, travelDays, noTravel }) => {
          items.push({ instructorId, locationId, period, role, workingDays, workingHours, travelDays, noTravel });
        }
      );
    }

    // Validar si hay datos para cerrar
    if (items.length <= 0) {
      ToastComponent({ text: t('Event cannot be closed.'), type: 'warning-toast' });
      return;
    } else {
      const response = await eventFormStore.closeEvent(items, this.identityService.userId);
      if (response?.isSuccess) {
        ToastComponent({ text: t('Event successfully closed!'), type: 'success-toast' });
        onClosed && onClosed();
      } else {
        (eventFormStore.state.result?.messages || []).forEach(msg => ToastComponent({ text: msg?.body, type: 'warning-toast' }));
      }
    }
  };

  handleOnChangeStatusAcceptClicked = async () => {
    const { eventFormStore, t, onClosed } = this.props;
    this.setState({ showChangeStatusModal: false });

    const response = await eventFormStore.nextStatus();
    if (response?.isSuccess) {
      ToastComponent({ text: t('Event successfully closed!'), type: 'success-toast' });
      onClosed && onClosed();
    } else {
      (eventFormStore.state.result?.messages || []).forEach(msg => ToastComponent({ text: msg?.body, type: 'warning-toast' }));
    }
  };

  isCloseButtonVisible = (): boolean => {
    const item = this.props.eventFormStore.state.item;
    if (!item) return false;
    const userCompliance =
      this.identityService.isInRole(Roles.Admin) || isUserEventOwner(item) || this.identityService.isInRole(Roles.Instructor);

    const eventCompliance =
      this.isCloseEventAllowedInCurrentEventState() &&
      (hasAdministrationRights(item) || (isUserAnEventActorWithValidations(item) && !hasUserAlreadyPerformedValidation(item))) &&
      !IdentityService.isWorker(this.identityService.getUserInfo());

    return userCompliance && eventCompliance;
  };

  isClosedButtonVisible = (): boolean => {
    const item = this.props.eventFormStore.state.item;
    if (!item) return false;
    return (
      this.isCloseEventAllowedInCurrentEventState() &&
      isUserAnEventActor(item) &&
      hasUserAlreadyPerformedValidation(item) &&
      !IdentityService.isWorker(this.identityService.getUserInfo())
    );
  };

  instructorCanClosedEvent = (): boolean => {
    const item = this.props.eventFormStore.state.item;
    if (isCurrentUserEventInstructor(item) && item.allTheoreticalFormAnswered != null && !item.allTheoreticalFormAnswered) return false;
    else return true;
  };

  closeConfirmModal = () => {
    this.setState({ showAlertCloseModal: false });
  };

  isCloseEventAllowedInCurrentEventState = (): boolean => {
    const item = this.props.eventFormStore.state.item;
    const nextStatus = getNextStatus(item);
    return nextStatus && nextStatus.toString() === 'Closed';
  };

  render() {
    const { t, readOnly, eventFormStore } = this.props;
    const isCloseButtonVisible = this.isCloseButtonVisible();
    const isClosedButtonVisible = this.isClosedButtonVisible();
    const { showAlertCloseModal, showChangeStatusModal } = this.state;

    return (
      <>
        {isCloseButtonVisible && (
          <button
            type="button"
            className="flex-1 py-2 rounded border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-all text-sm"
            disabled={!readOnly}
            onClick={this.handleOnCloseClicked}
          >
            Close Event
          </button>
        )}
        {isClosedButtonVisible && (
          <button
            type="button"
            className="flex-1 py-2 rounded border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-all text-sm"
            disabled
          >
            Closed Event
          </button>
        )}

        <ChangeStatusPopup
          eventFormStore={eventFormStore}
          open={showChangeStatusModal}
          onAcceptClicked={this.handleOnChangeStatusAcceptClicked}
          onCancelClicked={() => this.setState({ showChangeStatusModal: false })}
        />

        {showAlertCloseModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div className="bg-white rounded-md shadow-lg p-6 flex flex-col items-center max-w-sm">
              <p className="mb-4 text-center">{t('All Theoretical tests must be completed before closing the event.')}</p>
              <button
                className="flex-1 py-2 rounded border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-all text-sm"
                onClick={this.closeConfirmModal}
              >
                {t('Accept')}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }
}

function isGEA(eventType) {
  return eventType?.code === 'GEA';
}
function isGMA(eventType) {
  return eventType?.code === 'GMA';
}

export default withTranslation()(CloseButtonTw);
